import { useRef, useState } from "react";
/* -----------------Globals--------------- */
import { Button, Dialog } from "react-native-paper";

import { sendFeedback } from "../helpers";
/* -----------------Types--------------- */
import type { FeedbackParams } from "../types";

/* -----------------Child components--------------- */
import { FeedbackContent } from "@memorang/ui/src/components/FeedbackDialog/FeedbackContent";

import DialogWrapper from "@memorang/ui/src/components/DialogWrapper";
import ScrollContainer from "@memorang/ui/src/components/ScrollContainer";
import type { IconType } from "@memorang/ui/src/components/TextLoop";
import { useToast } from "@memorang/ui/src/components/Toast";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { HandleTracking } from "../../types";
import { events } from "../constants";

interface Props {
	title?: string;
	subtitle: string;
	visible: boolean;
	onDismiss: () => void;
	handleTracking: HandleTracking;
	feedbackDialogParams: FeedbackParams;
	subtitleSuffix?: string;
	feedbackPlaceholder?: string;
	icon?: string;
	cta?: string;
	handlePostFeedback: (feedback: string, email: string) => void;
	feedbackReasonList?: {
		title: string;
		description: string;
		icon: IconType;
		value: string;
	}[];
}

const FeedbackDialog = ({
	title,
	subtitle,
	visible,
	onDismiss,
	handleTracking,
	feedbackDialogParams,
	subtitleSuffix,
	feedbackPlaceholder,
	icon,
	cta,
	handlePostFeedback,
	feedbackReasonList,
}: Props) => {
	const { showToast } = useToast();
	const { t } = useTranslation();
	const feedbackContentRef = useRef<{ submit: () => void }>(null);
	const [sendingFeedback, setSendingFeedback] = useState(false);
	const [isValid, setIsValid] = useState(false);

	useEffect(() => {
		if (visible) {
			handleTracking(events.dialogOpened);
		}
	}, [handleTracking, visible]);

	const handleCloseDialog = () => {
		handleTracking(events.dialogClosed);
		onDismiss();
	};

	return (
		<DialogWrapper visible={visible} showCloseButton={false} maxWidth="xs">
			{icon && <Dialog.Icon icon={icon} />}
			<Dialog.Title
				style={{
					textAlign: "center",
				}}
			>
				{title || t("dialog.feedback.title")}
			</Dialog.Title>
			<ScrollContainer>
				<Dialog.Content>
					<FeedbackContent
						ref={feedbackContentRef}
						sendFeedback={sendFeedback}
						feedbackDialogParams={feedbackDialogParams}
						handlePostFeedback={handlePostFeedback}
						onDismiss={handleCloseDialog}
						subtitle={subtitle}
						subtitleSuffix={subtitleSuffix}
						feedbackPlaceholder={feedbackPlaceholder}
						feedbackReasonList={feedbackReasonList}
						showFeedbackConfirmationToast={() =>
							showToast({
								message: "Feedback submitted. Thank you!",
							})
						}
						onStateChange={(sending, valid) => {
							setSendingFeedback(sending);
							setIsValid(valid);
						}}
					/>
				</Dialog.Content>
			</ScrollContainer>
			<Dialog.Actions>
				<Button onPress={handleCloseDialog}>{t("common.cancel")}</Button>
				<Button
					onPress={() => feedbackContentRef.current?.submit()}
					loading={sendingFeedback}
					disabled={!isValid}
					mode="contained"
					style={{ width: 140 }}
				>
					{cta ?? t("report.sessionAnalysis.feedbackDialog.cta")}
				</Button>
			</Dialog.Actions>
		</DialogWrapper>
	);
};

export { FeedbackDialog };
